import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useTreeMutations } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks/TreeMutations/useTreeMutations'
import {
    useNotification,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { getRootArticleContentItemIds } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { mergeContentItems } from 'components/WorkspaceEditor/utils'
import { getActiveTabLabel } from 'components/WorkspaceEditor/utils/workspaceEditorUtils'
import { useCallback, useEffect } from 'react'
import { useShallowSelector, useWorkspace2Store } from '../../../Workspace2Store'
import { ContentItemViewNodesType } from '../../../types'
import {
    preventEventDefault,
    tryGetShortcutKey,
} from '../../../utils/shortcutKeyUtils'
import {
    ContentItemViewInput,
    usePromoteDemoteContentViewMutation,
} from '../api/mutations/promoteDemoteContentView'
import {
    NO_ACTIVE_ID,
    NO_VALID_DEMOTE_PARENT_ERROR,
    NO_VALID_PROMOTE_TARGET_ERROR,
    PromoteDemoteActions,
} from '../constants'
import { pendingPromoteDemoteMove } from '../services/localPromoteDemote'
import {
    usePromoteDemoteSelector,
    usePromoteDemoteStore,
} from '../store/PromoteDemoteStore'
import { PromoteDemoteAction } from '../types'
import { useFocusRestoration } from './useFocusRestoration'
import { useLocalPromoteDemote } from './useLocalPromoteDemote'
import { usePromoteDemoteService } from './usePromoteDemoteService'

/**
 * Hook that encapsulates promote/demote functionality for text content elements.
 *
 * @returns An object containing state and functions for promote/demote operations
 */
export const usePromoteDemote = () => {
    const { buildInputVariables } = usePromoteDemoteService()
    const { executeWithOptimistic } = useTransactionManager()
    const { promoteDemoteContentView } = usePromoteDemoteContentViewMutation()
    const { reRenderTree } = useTreeMutations()
    const { restoreFocus } = useFocusRestoration()
    const notification = useNotification()
    const {
        isContentTeamFromStudioPage,
        isEditingPlainText,
        contentItemViewNodes,
        newParagraphToAdd,
        treeData,
        projectId,
        activeTab,
    } = useShallowSelector((s) => ({
        isContentTeamFromStudioPage: s.isContentTeamFromStudioPage,
        isEditingPlainText: s.isEditingPlainText,
        contentItemViewNodes: s.contentItemViewNodes,
        newParagraphToAdd: s.textContentElementToAdd,
        treeData: s.treeData,
        projectId: s.projectId,
        activeTab: s.activeTab,
    }))
    const { id: nodeId } = useElementItemShallowSelector(
        (s) => s.elementItemValue
    )

    const {
        canBePromoted,
        canBeDemoted,
        contentItemToPromoteDemote: activeItem,
        isPromoteDemoteInFlight,
        setPromoteDemoteInFlight,
        setContentItemToPromoteDemote,
        resetPromoteDemoteState: resetPromoteDemote,
    } = usePromoteDemoteSelector((s) => ({
        canBePromoted: s.canBePromoted,
        canBeDemoted: s.canBeDemoted,
        contentItemToPromoteDemote: s.contentItemToPromoteDemote,
        isPromoteDemoteInFlight: s.isPromoteDemoteInFlight,
        setPromoteDemoteInFlight: s.setPromoteDemoteInFlight,
        setContentItemToPromoteDemote: s.setContentItemToPromoteDemote,
        resetPromoteDemoteState: s.resetPromoteDemoteState,
    }))

    const isCreatingNewParagraph = Boolean(newParagraphToAdd)
    const isTempNodeActive =
        isCreatingNewParagraph && activeItem?.id === newParagraphToAdd?.id
    const isBlockedByOtherNewParagraph =
        isCreatingNewParagraph && !isTempNodeActive
    const canPromote =
        !isBlockedByOtherNewParagraph &&
        !isPromoteDemoteInFlight &&
        canBePromoted(isContentTeamFromStudioPage)
    const canDemote =
        !isBlockedByOtherNewParagraph &&
        !isPromoteDemoteInFlight &&
        canBeDemoted(isContentTeamFromStudioPage)

    const register = useCallback(
        (
            contentItem: Partial<TextContentElement>,
            isInProject: boolean = false,
            contentItemViewNodes: Partial<ContentItemViewNodesType>[] = []
        ) => {
            setContentItemToPromoteDemote({
                contentItemToPromoteDemote: contentItem,
                isInProject,
                contentItemViewNodes,
            })
        },
        [setContentItemToPromoteDemote]
    )

    const { handleLocalPromoteDemote } = useLocalPromoteDemote(
        activeItem,
        isTempNodeActive,
        contentItemViewNodes,
        treeData,
        register
    )

    /**
     * Sends a local move (queued by `useLocalPromoteDemote`) to the server.
     * No-op if nothing is pending — a burst of Tab presses only ever leaves one id.
     */
    const commitPendingPromoteDemote = useCallback(async () => {
        const activeItemId = pendingPromoteDemoteMove.take()
        if (!activeItemId) return

        const variables = buildInputVariables(activeItemId)
        if (!variables) return

        // Fresh read: the local move already wrote the store by now.
        const currentNodes = useWorkspace2Store.getState().contentItemViewNodes
        const rootArticleContentItemIds = getRootArticleContentItemIds(
            currentNodes ?? [],
            [activeItemId]
        )

        // responseNodes is populated by asyncAction before successCbFn runs.
        let responseNodes: Partial<ContentItemViewNodesType>[] = []

        setPromoteDemoteInFlight(true)
        await executeWithOptimistic({
            fallbackState: currentNodes,
            asyncAction: async () => {
                const contentItemViewInput: ContentItemViewInput = {
                    projectId: projectId ?? '',
                    templateItemPartType: getActiveTabLabel(activeTab) ?? '',
                    isTrackChangeToggleOn: true,
                    rootArticleContentItemId: rootArticleContentItemIds,
                    nodeId: nodeId ?? '',
                    features: [],
                }
                const result = await promoteDemoteContentView(
                    variables,
                    contentItemViewInput
                )
                responseNodes =
                    result?.data?.promoteDemoteContentView
                        ?.contentItemViewNodes ?? []
                return result
            },
            successCbFn: () => {
                resetPromoteDemote()
                const merged = mergeContentItems(
                    currentNodes ?? [],
                    responseNodes
                )
                reRenderTree({ updatedContentItemViewNodes: merged })
                restoreFocus(activeItemId)
                setPromoteDemoteInFlight(false)
            },
            errorCbFn: () => {
                setPromoteDemoteInFlight(false)
            },
        })
    }, [
        buildInputVariables,
        executeWithOptimistic,
        promoteDemoteContentView,
        resetPromoteDemote,
        reRenderTree,
        restoreFocus,
        setPromoteDemoteInFlight,
        projectId,
        activeTab,
        nodeId,
    ])

    const handlePromoteDemoteMutation = useCallback(
        async (action: PromoteDemoteAction, isFromShortcut: boolean = false) => {
            const activeItemId = activeItem?.id

            if (usePromoteDemoteStore.getState().isPromoteDemoteInFlight) return
            if (isBlockedByOtherNewParagraph) return
            if (!activeItemId)
                return notification.error({
                    message: `${NO_ACTIVE_ID} ${action}`,
                })

            // Validates and re-parents locally; temp-node and saved-content share this move.
            handleLocalPromoteDemote(action)

            // Temp node: nothing to commit, level saves with the paragraph.
            // Shortcut: defer the commit to the next blur/Ctrl+S/Enter.
            if (isTempNodeActive || isFromShortcut) return
            return commitPendingPromoteDemote()
        },
        [
            activeItem,
            isBlockedByOtherNewParagraph,
            isTempNodeActive,
            handleLocalPromoteDemote,
            commitPendingPromoteDemote,
            notification,
        ]
    )

    // handle keyboard events for promote/demote shortcuts
    const handlePromoteDemoteShortcutKey = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            const { Promote, Demote } = PromoteDemoteActions
            const isPromoteKey = tryGetShortcutKey(Promote, event)
            const isDemoteKey = tryGetShortcutKey(Demote, event)

            if (isPromoteKey && canPromote) {
                // Prevent default Shift+Tab behavior to stop focus from moving
                preventEventDefault(event)
                return handlePromoteDemoteMutation(Promote, true)
            }
            if (isDemoteKey && canDemote) {
                // Prevent default Tab behavior to stop focus from moving to next element
                preventEventDefault(event)
                return handlePromoteDemoteMutation(Demote, true)
            }

            // Eligible for at least one action but not the one pressed — say
            // why instead of doing nothing.
            if ((isPromoteKey || isDemoteKey) && (canPromote || canDemote)) {
                preventEventDefault(event)
                notification.error(
                    isDemoteKey
                        ? NO_VALID_DEMOTE_PARENT_ERROR
                        : NO_VALID_PROMOTE_TARGET_ERROR
                )
            }
        },
        [canPromote, canDemote, handlePromoteDemoteMutation, notification]
    )

    /**
     * side-effect: reset promote/demote state when nothing is selected
     * Toggles the Promote/Demote buttons on action bar
     */
    useEffect(() => {
        let resetId: NodeJS.Timeout

        if (!isEditingPlainText && activeItem) {
            resetId = setTimeout(() => {
                // reset means toggle the promote/demote buttons on action bar
                resetPromoteDemote()
            }, 100) // 100ms delay to ensure that the current item is evaluated by action bar before resetting the state
        }

        return () => clearTimeout(resetId)
    }, [isEditingPlainText, activeItem, resetPromoteDemote])

    return {
        canPromote,
        canDemote,
        isPromoteDemoteInFlight,
        activeItem,
        register,
        handlePromoteDemoteShortcutKey,
        handlePromoteDemoteMutation,
        commitPendingPromoteDemote,
        resetPromoteDemote,
    }
}
